<template>
  <div
    class="tw-mb-4 tw-inline-flex tw-flex-col tw-bg-white tw-p-6 tw-rounded-lg tw-border tw-border-primary-grey-100 tw-w-full"
  >
    <div class="tw-flex tw-items-center tw-mb-4">
      <div
        class="tw-mr-4 tw-p-4 tw-bg-primary-grey-50 tw-inline-flex tw-justify-center tw-items-center tw-rounded-lg tw-self-start"
      >
        <TM365Icon class="tw-w-8 tw-h-8" />
      </div>
      <div class="tw-flex tw-flex-col">
        <div class="tw-flex tw-items-center tw-gap-2">
          <p class="tw-text-lg tw-font-bold">
            {{ t("m365-connection-title") }}
          </p>
          <cl-pill
            v-if="inUpdateMode && updateStatus === 'success'"
            :variant="'success'"
            :uppercase="false"
            data-test-id="m365-connected-pill"
          >
            {{ t("connected") }}
          </cl-pill>
          <cl-pill
            v-else-if="inUpdateMode"
            :variant="'warning'"
            :uppercase="false"
            data-test-id="m365-action-required-pill"
          >
            {{ t("action-required") }}
          </cl-pill>
        </div>
        <p>
          {{ t("m365-connection-value") }}
        </p>
      </div>
      <button
        class="tw-flex tw-items-center tw-justify-center tw-ml-auto tw-mt-2"
        @click="isExpanded = !isExpanded"
      >
        <cb-icon-chevron-down
          class="tw-w-6 tw-h-6 tw-transition-transform tw-duration-200"
          :class="{ 'tw-rotate-180': isExpanded }"
        />
      </button>
    </div>
    <div v-if="isConnected && !inUpdateMode" class="tw-flex tw-justify-between">
      <div class="tw-flex tw-items-center">
        <span class="tw-text-sm tw-text-primary-grey-300 tw-mr-8">{{
          t("status")
        }}</span>
        <div
          class="tw-w-3 tw-h-3 tw-rounded-full tw-mr-2"
          :class="
            isConnected ? 'tw-bg-system-success-500' : 'tw-bg-system-error-500'
          "
        ></div>
        <span class="tw-text-sm">{{
          isConnected ? t("connected") : t("not-connected")
        }}</span>
      </div>
      <p class="tw-text-xs tw-text-primary-grey-300">
        {{ t("last-update") }} {{ lastUpdate }}
      </p>
    </div>
    <div
      v-show="isExpanded"
      class="tw-pt-4 tw-mt-4 tw-border-t tw-border-primary-grey-100"
    >
      <cb-notification-bar
        v-if="showUpdatePanel"
        :variant="updateBannerVariant"
        :show-close-button="true"
        test-id="m365-permissions-update-banner"
        class="tw-mb-4"
        @on-close="isUpdateBannerDismissed = true"
      >
        <template #title>
          {{ updateBannerTitle }}
        </template>
        <template #description>
          {{ updateBannerDescription }}
        </template>
        <template #primaryCta v-if="updateStatus !== 'success'">
          <authorisation-wrapper
            :resource="ADMINISTRATORS"
            :action="WRITE"
            v-slot="{ isAllowed: isWriteAllowed }"
          >
            <cl-button
              variant="default"
              size="small"
              @on-click="$emit('update')"
              :disabled="isRequestPending"
              :readonly="!isWriteAllowed"
              data-test-id="m365-update-cta-button"
            >
              <span v-if="isUpdating">
                <cl-spinner
                  :loading-text="$t('loading')"
                  :size="'small'"
                  class="tw-mr-2"
                />
                <span>{{ t("connecting-microsoft-365") }}</span>
              </span>
              <span v-else>{{ updateBannerCta }}</span>
            </cl-button>
          </authorisation-wrapper>
        </template>
      </cb-notification-bar>
      <div class="tw-text-base tw-mb-4">
        <p class="tw-mb-2">{{ t("validate-m365-domains-lead-in") }}</p>
        <ul class="tw-list-none">
          <li class="tw-flex tw-items-center tw-gap-2">
            <CbIconTick color="#65C091" />
            {{ t("m365-global-admin") }}
          </li>
          <li class="tw-flex tw-items-center tw-gap-2">
            <CbIconTick color="#65C091" />
            {{ t("m365-credentials") }}
          </li>
        </ul>
      </div>
      <div
        v-if="isConnected"
        class="tw-flex tw-justify-end tw-items-center tw-gap-2"
      >
        <t-link
          :href="'https://support.titanhq.com/en/62975-connect-to-microsoft-365.html'"
          :is-external="true"
          :string="t('learn-more')"
          data-test-id="m365-learn-more-link"
          class="tw-underline tw-mr-4"
        />
        <cl-button
          variant="secondary"
          :href="M365_ACCOUNT_LINK"
          @click="navigateToLink"
        >
          {{ t("open-microsoft-365") }}
          <cb-icon-external-link :width="20" />
        </cl-button>
        <authorisation-wrapper
          v-if="isAzureAppChooserEnabled"
          :resource="ADMINISTRATORS"
          :action="WRITE"
          v-slot="{ isAllowed: isWriteAllowed }"
        >
          <cl-button
            v-if="connectedApp === 'old'"
            variant="secondary"
            @on-click="$emit('connect')"
            :disabled="isRequestPending"
            :readonly="!isWriteAllowed"
            data-test-id="m365-reconnect-new-app-button"
          >
            <span v-if="pendingApp === 'new'">
              <cl-spinner
                :loading-text="$t('loading')"
                :size="'small'"
                class="tw-mr-2"
              />
              <span>{{ t("connecting-microsoft-365") }}</span>
            </span>
            <span v-else>Connect M365 (New App)</span>
          </cl-button>
          <cl-button
            v-else-if="connectedApp === 'new'"
            variant="secondary"
            @on-click="$emit('connect-old')"
            :disabled="isRequestPending"
            :readonly="!isWriteAllowed"
            data-test-id="m365-reconnect-old-app-button"
          >
            <span v-if="pendingApp === 'old'">
              <cl-spinner
                :loading-text="$t('loading')"
                :size="'small'"
                class="tw-mr-2"
              />
              <span>Connecting Microsoft 365 (old app)</span>
            </span>
            <span v-else>Connect M365 (Old App)</span>
          </cl-button>
        </authorisation-wrapper>
      </div>
      <div
        v-else-if="!isConnected && !inUpdateMode"
        class="tw-flex tw-justify-end tw-items-center tw-gap-2"
      >
        <t-link
          :href="'https://support.titanhq.com/en/62975-connect-to-microsoft-365.html'"
          :is-external="true"
          :string="t('learn-more')"
          data-test-id="m365-contact-support-link"
          class="tw-underline tw-mr-4"
        />
        <authorisation-wrapper
          :resource="ADMINISTRATORS"
          :action="WRITE"
          v-slot="{ isAllowed: isWriteAllowed }"
        >
          <cl-button
            variant="secondary"
            @on-click="$emit('connect')"
            :disabled="isRequestPending"
            :readonly="!isWriteAllowed"
            data-test-id="m365-connect-button"
          >
            <span v-if="isPrimaryConnectPending">
              <cl-spinner
                :loading-text="$t('loading')"
                :size="'small'"
                class="tw-mr-2"
              />
              <span>{{ t("connecting-microsoft-365") }}</span>
            </span>
            <span v-else>{{ t("connect-microsoft-365") }}</span>
          </cl-button>
          <cl-button
            v-if="isAzureAppChooserEnabled"
            variant="secondary"
            @on-click="$emit('connect-old')"
            :disabled="isRequestPending"
            :readonly="!isWriteAllowed"
            data-test-id="m365-connect-old-app-button"
          >
            <span v-if="pendingApp === 'old'">
              <cl-spinner
                :loading-text="$t('loading')"
                :size="'small'"
                class="tw-mr-2"
              />
              <span>Connecting Microsoft 365 (old app)</span>
            </span>
            <span v-else>Connect M365 (old app)</span>
          </cl-button>
        </authorisation-wrapper>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import TM365Icon from "@/components/TM365Icon/TM365Icon.vue";
import TLink from "@/components/TLink";
import { CbIconTick } from "@/components/cybersentriq-ui";
import { AuthorisationWrapper, ADMINISTRATORS, WRITE } from "@/authorisation";
import { useFlag } from "@/composables/useFlags/useFlag";

interface M365ConnectionCardProps {
  isConnected: boolean;
  lastUpdate: string;
  isRequestPending: boolean;
  pendingApp?: "old" | "new" | null;
  connectedApp?: "old" | "new" | null;
  primaryConnectApp?: "old" | "new";
  needsAppUpdate?: boolean;
  updateStatus?: "idle" | "success" | "error";
}

const { t } = useI18n();
const { isFeatureAvailable: isAzureAppChooserEnabled } = useFlag(
  "M365_AZURE_APP_CHOOSER",
);
const M365_ACCOUNT_LINK =
  "https://portal.azure.com/#view/Microsoft_AAD_IAM/StartboardApplicationsMenuBlade/~/AppAppsPreview";

const props = defineProps<M365ConnectionCardProps>();

const updateStatus = computed(() => props.updateStatus ?? "idle");
const inUpdateMode = computed(
  () => !!props.needsAppUpdate || updateStatus.value !== "idle",
);

const isExpanded = ref(!props.isConnected || inUpdateMode.value);
const isUpdateBannerDismissed = ref(false);
const showUpdatePanel = computed(
  () => inUpdateMode.value && !isUpdateBannerDismissed.value,
);
const isUpdating = computed(
  () =>
    props.isRequestPending &&
    props.pendingApp === "new" &&
    updateStatus.value !== "success",
);

// The primary connect action targets the new Azure app whenever it is
// configured, otherwise it falls back to the old app (prod, until the new app
// is registered). Spin the primary button while its target app is pending.
const isPrimaryConnectPending = computed(
  () => props.pendingApp === (props.primaryConnectApp ?? "new"),
);

const updateBannerVariant = computed(() =>
  updateStatus.value === "success"
    ? "success"
    : updateStatus.value === "error"
      ? "error"
      : "warning",
);
const updateBannerTitle = computed(
  () =>
    ({
      idle: t("permissions-update-required"),
      error: t("update-permissions-failed"),
      success: t("update-permissions-complete"),
    })[updateStatus.value],
);
const updateBannerDescription = computed(
  () =>
    ({
      idle: t("permissions-update-required-description"),
      error: t("update-permissions-failed-description"),
      success: t("update-permissions-complete-description"),
    })[updateStatus.value],
);
const updateBannerCta = computed(() =>
  updateStatus.value === "error" ? t("retry-update") : t("update-permissions"),
);

watch(
  () => props.isConnected,
  (connected) => {
    if (connected && !inUpdateMode.value) isExpanded.value = false;
  },
);

// Re-surface the banner whenever the update status changes: the update flow
// suppresses the default toast, so a dismissed banner would otherwise leave a
// failed (or completed) migration with no feedback at all.
watch(
  () => updateStatus.value,
  () => {
    isUpdateBannerDismissed.value = false;
  },
);
const navigateToLink = () => {
  window.open(M365_ACCOUNT_LINK, "_blank");
};

defineEmits<{
  connect: [];
  "connect-old": [];
  update: [];
}>();
</script>
